import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

function formatDateRange(arrival, departure) {
  if (!arrival && !departure) return "";
  if (arrival && departure) return `${arrival} → ${departure}`;
  return arrival || departure || "";
}

export default function GuestChoicesPage() {
  const { guestId } = useParams();
  const [data, setData] = useState(null);
  const [choices, setChoices] = useState([]);
  const [maxChoices, setMaxChoices] = useState(5);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [activityMap, setActivityMap] = useState({});
  const [skipSelections, setSkipSelections] = useState([false, false, false]);
  const [addedCostsAck, setAddedCostsAck] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setStatus("");
      const res = await fetch(`/api/guests/${guestId}/choices`);
      const json = await res.json();
      if (!cancelled) {
        setData(json);
        const nextMax = Math.max(1, Math.min(10, Number(json.maxChoices || 5)));
        setMaxChoices(nextMax);
        const initial = Array.isArray(json.choices) ? json.choices.slice(0, nextMax) : [];
        while (initial.length < nextMax) initial.push("");
        setChoices(initial);
        const selections = Array.isArray(json.skipSelections) ? json.skipSelections : [];
        setSkipSelections([
          !!selections[0],
          !!selections[1],
          !!selections[2],
        ]);
        setAddedCostsAck(!!json.addedCosts);
        const map = {};
        (json.activities || []).forEach((a) => {
          map[a.id] = a;
        });
        setActivityMap(map);
      }
    }
    if (guestId) load();
    return () => {
      cancelled = true;
    };
  }, [guestId]);

  const options = useMemo(() => data?.activities || [], [data]);
  const hasAddedCosts = useMemo(() => {
    return choices.some((choiceId) => {
      if (!choiceId) return false;
      const activity = activityMap[choiceId];
      if (!activity) return false;
      const costPerson = Number(activity.addedCostPerPerson ?? 0);
      const costActivity = Number(activity.addedCostPerActivity ?? 0);
      return costPerson > 0 || costActivity > 0;
    });
  }, [choices, activityMap]);

  function updateChoice(idx, value) {
    const next = [...choices];
    next[idx] = value || "";
    setChoices(next);
  }

  function isSelected(id, idx) {
    return choices.some((c, i) => i !== idx && c === id);
  }

  async function save() {
    setSaving(true);
    setStatus("");
    try {
      const res = await fetch(`/api/guests/${guestId}/choices`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          choices: choices.slice(0, maxChoices),
          skipSelections,
          ...(hasAddedCosts ? { addedCosts: addedCostsAck } : {}),
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        setStatus(json?.error || "Failed to save choices");
        return;
      }
      setStatus("Saved");
    } finally {
      setSaving(false);
    }
  }

  if (!data) {
    return <div style={{ padding: 16 }}>Loading choices…</div>;
  }

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: 16 }}>
      <div style={{ border: "1px solid #ddd", borderRadius: 12, padding: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 800 }}>{data.name || "Guest"}</div>
        {formatDateRange(data.arrivalDate, data.departureDate) && (
          <div style={{ marginTop: 6, opacity: 0.7 }}>
            {formatDateRange(data.arrivalDate, data.departureDate)}
          </div>
        )}
        {data.infoText ? (
          <div
            style={{
              marginTop: 8,
              fontSize: 13,
              fontWeight: 700,
              opacity: 0.9,
              whiteSpace: "pre-wrap",
            }}
          >
            {data.infoText}
          </div>
        ) : null}
        <div style={{ marginTop: 8 }}>
          <a
            href="https://www.brushcreekranch.com/activities#activities-grid"
            target="_blank"
            rel="noreferrer"
          >
            View all activities
          </a>
        </div>
      </div>

      <style>{`
        @media (max-width: 640px) {
          .choices-grid {
            grid-template-columns: 1fr !important;
          }
          .choice-select {
            font-size: 16px;
          }
        }
      `}</style>

      <div style={{ marginTop: 16, border: "1px solid #ddd", borderRadius: 12, padding: 16 }}>
        <div style={{ fontWeight: 800, marginBottom: 10 }}>
          Top {maxChoices} Activity Choices
        </div>
        {(data.skipItems || [])
          .map((item, idx) => ({ item, idx }))
          .filter(({ item }) => item?.label)
          .map(({ item, idx }) => (
            <label
              key={`${item.label}-${idx}`}
              style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}
            >
              <input
                type="checkbox"
                checked={!!skipSelections[idx]}
                onChange={(e) => {
                  const next = [...skipSelections];
                  next[idx] = e.target.checked;
                  setSkipSelections(next);
                }}
              />
              <span style={{ fontSize: 12, fontWeight: 700 }}>{item.label}</span>
            </label>
          ))}
        <div className="choices-grid" style={{ display: "grid", gap: 10 }}>
          {Array.from({ length: maxChoices }).map((_, idx) => {
            const selected = choices[idx] || "";
            const selectedActivity = activityMap[selected];
            return (
              <label key={idx} style={{ display: "grid", gap: 6 }}>
                <span style={{ fontWeight: 700 }}>Choice {idx + 1}</span>
                <select
                  className="choice-select"
                  value={selected}
                  onChange={(e) => updateChoice(idx, e.target.value)}
                >
                  <option value="">Select activity…</option>
                  {options.map((a) => (
                    <option key={a.id} value={a.id} disabled={isSelected(a.id, idx)}>
                      {a.name || "(Unnamed activity)"}
                    </option>
                  ))}
                </select>
                {selectedActivity?.url ? (
                  <a href={selectedActivity.url} target="_blank" rel="noreferrer">
                    View activity
                  </a>
                ) : null}
                {selectedActivity?.description ? (
                  <div style={{ fontSize: 12, opacity: 0.7 }}>
                    {selectedActivity.description}
                  </div>
                ) : null}
              </label>
            );
          })}
        </div>
        {hasAddedCosts && (
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
            <input
              type="checkbox"
              checked={addedCostsAck}
              onChange={(e) => setAddedCostsAck(e.target.checked)}
            />
            <span style={{ fontSize: 12, fontWeight: 700 }}>
              I understand that some of the activities I am requesting have added costs
            </span>
          </label>
        )}
        <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center" }}>
          <button onClick={save} disabled={saving || (hasAddedCosts && !addedCostsAck)}>
            {saving ? "Saving…" : "Save Choices"}
          </button>
          {status && <span style={{ fontSize: 12, opacity: 0.7 }}>{status}</span>}
          {hasAddedCosts && !addedCostsAck && (
            <span style={{ fontSize: 12, opacity: 0.7 }}>
              Please confirm the added cost acknowledgement.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
